import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cm from 'classnames';
import pathfinder from '@fiverr-private/pathfinder';
import { formatDate } from '@fiverr-private/futile';
import { I18n } from '@fiverr-private/i18n-react';
import { Avatar, Button, Icon } from '@fiverr-private/fit';
import { DATETIME_ELEMENT_STYLES, dateTimeFormat, numberFormat } from '@fiverr-private/localization';
import { Rating } from '@fiverr-private/orca';
import { makersExperience } from '@fiverr-private/logo_maker_analytics';
import { DOMAIN_SOURCES } from '@fiverr-private/variations_display';
import { message } from './icons';
import './style.scss';

const { reportSellerAvatarClicked } = makersExperience.chooseVariation;

/**
 * @param {number | string} registeredAt
 */
const formatSellerRegistrationDate = (registeredAt) => {
    const date = typeof registeredAt === 'number' ? new Date(registeredAt * 1000) : new Date(registeredAt);
    const DATE_FORMAT = { year: 'numeric', month: 'short', day: false };

    return dateTimeFormat({
        value: date,
        options: {
            fallbackValue: formatDate(date, DATE_FORMAT),
            year: DATETIME_ELEMENT_STYLES.NUMERIC,
            month: DATETIME_ELEMENT_STYLES.SHORT,
        },
    });
};

const VariationSellerCard = ({ seller, className }) => {
    const { username, description, profilePhoto, memberSince, rating, ratingsCount, country, responseTime } = seller;

    const [sellerLink, contactLink] = useMemo(
        () => [
            pathfinder('short_user', { id: username }, { query: { source: DOMAIN_SOURCES.LOGO_MAKER } }),
            pathfinder('inbox_show', { username }, { query: { source: DOMAIN_SOURCES.LOGO_MAKER } }),
        ],
        [username]
    );

    const ratingFormatted = rating
        ? numberFormat({
              value: rating,
              options: {
                  fallbackValue: rating,
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
              },
          })
        : null;
    const ratingsCountFormatted = ratingsCount
        ? numberFormat({
              value: ratingsCount,
              options: {
                  fallbackValue: ratingsCount,
              },
          })
        : 0;
    const fromDateFormatted = formatSellerRegistrationDate(memberSince);

    return (
        <div className={cm(className, 'variation-seller-card')}>
            <h6 className="variation-seller-card-title">
                <I18n k="logo_maker_perseus.choose_variation.seller_card.about_designer" />
            </h6>
            <div className="variation-seller-main-info">
                <Avatar src={profilePhoto} username={username} size={50} />
                <div className="variation-seller-info-wrapper">
                    <a
                        className="variation-seller-username"
                        href={sellerLink}
                        target="_blank"
                        rel="noreferrer"
                        onClick={reportSellerAvatarClicked}
                    >
                        {username}
                    </a>
                    {!!ratingFormatted && !!ratingsCountFormatted && (
                        <Rating
                            className="variation-seller-card-rating"
                            scoreClassName="variation-seller-card-score"
                            ratingsCountClassName="variation-seller-card-ratings-count"
                            score={ratingFormatted}
                            ratingsCount={ratingsCountFormatted}
                        />
                    )}
                </div>
            </div>
            <div className="variation-seller-description">{description}</div>
            <hr className="variation-seller-card-separator" />
            <div className="variation-seller-stats">
                <div className="variation-seller-stats-column">
                    <div className="variation-seller-stat">
                        <span>
                            <I18n k="logo_maker_perseus.choose_variation.seller_card.from" />
                        </span>
                        <b>{country}</b>
                    </div>
                    <div className="variation-seller-stat">
                        <span>
                            <I18n k="logo_maker_perseus.choose_variation.seller_card.member_since" />
                        </span>
                        <b>{fromDateFormatted}</b>
                    </div>
                </div>
                <div className="variation-seller-stats-column">
                    {!!responseTime && (
                        <div className="variation-seller-stat">
                            <span>
                                <I18n k="logo_maker_perseus.choose_variation.seller_card.avg_response_time" />
                            </span>
                            <b>
                                <I18n
                                    k="logo_maker_perseus.choose_variation.seller_card.number_of_hours"
                                    params={{ count: responseTime }}
                                />
                            </b>
                        </div>
                    )}
                </div>
            </div>
            <Button
                href={contactLink}
                className="variation-seller-card-contact-btn"
                textColor={Button.TEXT_COLORS.DARK_GREY}
                color={Button.COLORS.LIGHT_GREY}
            >
                <Icon className="variation-seller-card-message-icon">{message}</Icon>
                <I18n k="logo_maker_perseus.choose_variation.seller_card.contact_designer" />
            </Button>
        </div>
    );
};

VariationSellerCard.propTypes = {
    seller: PropTypes.object,
    className: PropTypes.string,
};

export default VariationSellerCard;
